import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import type { Section } from '../types/onboarding';
import { scrollToSection } from '../utils/scrollToSection';
interface SectionNavProps {
  sections: Section[];
  activeId: string;
  isDone: (id: string) => boolean;
  doneCount: number;
  /** 0–1 page scroll progress driving the emerald indicator. */
  scrollProgress: number;
}
function ProgressTrack({
  progress,
  className = ''
}: {
  progress: number;
  className?: string;
}) {
  return <div className={`overflow-hidden rounded-full bg-white/10 ${className}`} aria-hidden="true" data-id="element-116" data-mp="1gteip56i4sl1">
      <div className="h-full rounded-full bg-[#10b981] transition-[width] duration-300 ease-out" style={{
      width: `${Math.round(progress * 100)}%`
    }} data-id="element-117" data-mp="1c024zq3d2672" />
    </div>;
}
function StepDot({
  number,
  done
}: {
  number: number;
  done: boolean;
}) {
  return <span aria-hidden="true" className={['flex h-6 w-6 shrink-0 items-center justify-center rounded-md border font-mono text-[10px] font-semibold transition-colors duration-200', done ? 'border-[#10b981]/50 bg-[#10b981] text-black' : 'border-white/10 bg-white/[0.06] text-white/60'].join(' ')} data-id="element-118" data-mp="1zqxrc7p1s96x">
      {done ? <CheckIcon className="h-3.5 w-3.5" data-id="element-119" data-mp="xg444tfmqr8k" /> : String(number).padStart(2, '0')}
    </span>;
}
export function SectionNav({
  sections,
  activeId,
  isDone,
  doneCount,
  scrollProgress
}: SectionNavProps) {
  const [open, setOpen] = useState(false);
  const active = sections.find(section => section.id === activeId);
  return <>
      {/* Mobile: sticky compact jump bar */}
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#0a0a0a]/95 text-white backdrop-blur-md lg:hidden" data-id="element-120" data-mp="ai0vmfl4lswu">
        <div className="px-5 pb-3 pt-3" data-id="element-121" data-mp="24tyv3ptmvafj">
          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex min-h-[52px] w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#111214] px-4 text-left" data-id="element-122" data-mp="24v0goo89zfbi">
            <span className="min-w-0" data-id="element-123" data-mp="3cs3blz6ktewd">
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#10b981]" data-id="element-124" data-mp="3cvhbn0rrpc0n">
                Step {active ? String(active.number).padStart(2, '0') : '01'} /{' '}
                {String(sections.length).padStart(2, '0')}
              </span>
              <span className="mt-1 block truncate font-mono text-[14px] font-semibold tracking-tight text-white" data-id="element-125" data-mp="4c1h1p4dys2j">
                {active ? active.navLabel : sections[0].navLabel}
              </span>
            </span>
            <ChevronDownIcon className={`h-5 w-5 shrink-0 text-white/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-126" data-mp="3roar4m71hs5b" />
          </button>

          <ProgressTrack progress={scrollProgress} className="mt-3 h-1" data-id="element-127" data-mp="o3546kmkk19p" />

          {open && <nav aria-label="Sections" className="mt-3 rounded-xl border border-white/10 bg-[#111214] p-2" data-id="element-128" data-mp="j8r0zw1o0puh">
              <ul data-id="element-129" data-mp="vlw4uhr87t9y">
                {sections.map(section => <li key={section.id} data-id="element-130" data-mp="10k600scyatvk">
                    <a href={`#${section.id}`} onClick={event => {
                event.preventDefault();
                setOpen(false);
                scrollToSection(section.id);
              }} aria-current={section.id === activeId ? 'true' : undefined} className={['flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-[15px] transition-colors duration-200', section.id === activeId ? 'bg-[#10b981]/10 font-semibold text-[#10b981]' : 'text-white/80'].join(' ')} data-id="element-131" data-mp="10j4s2wr8eni2">
                      <StepDot number={section.number} done={isDone(section.id)} data-id="element-132" data-mp="1jngbs1z83h5j" />
                      <span className="truncate" data-id="element-133" data-mp="1jlda2kopjzkz">{section.navLabel}</span>
                    </a>
                  </li>)}
                <li data-id="element-134" data-mp="1tdsudonh9091">
                  <a href="#need-a-hand" onClick={event => {
                event.preventDefault();
                setOpen(false);
                scrollToSection('need-a-hand');
              }} className="flex min-h-[48px] items-center gap-3 rounded-lg px-3 text-[15px] text-white/80" data-id="element-135" data-mp="1tc7ztz15xhb6">
                    <span className="h-6 w-6 shrink-0" aria-hidden="true" data-id="element-136" data-mp="1an1lspbbddvp" />
                    Need a hand?
                  </a>
                </li>
              </ul>
            </nav>}
        </div>
      </div>

      {/* Desktop: sticky sidebar */}
      <nav aria-label="Sections" className="sticky top-8 hidden h-fit w-64 shrink-0 text-white lg:block xl:w-72" data-id="element-137" data-mp="2m3rkzohbl5ft">
        <div className="rounded-2xl border border-white/10 bg-[#111214] p-5 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.9)]" data-id="element-138" data-mp="2ut7yzqycrj9">
          <div className="flex items-baseline justify-between" data-id="element-139" data-mp="7vqrbfnp7rmc">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#10b981]" data-id="element-140" data-mp="3q0btjkwhyr5v">
              Your progress
            </p>
            <p className="font-mono text-[11px] text-white/60" data-id="element-141" data-mp="3uzf059s4l5fy">
              {String(doneCount).padStart(2, '0')}/{String(sections.length).padStart(2, '0')}
            </p>
          </div>

          <div role="progressbar" aria-valuenow={doneCount} aria-valuemin={0} aria-valuemax={sections.length} aria-label="Onboarding progress" className="mt-3" data-id="element-142" data-mp="qjdn4t8bz9xe">
            <ProgressTrack progress={scrollProgress} className="h-1.5" data-id="element-143" data-mp="15hxuqm89ndx4" />
          </div>

          <ul className="mt-5 space-y-1" data-id="element-144" data-mp="15g39t7wzvlt1">
            {sections.map(section => {
            const isActive = section.id === activeId;
            return <li key={section.id} data-id="element-145" data-mp="1k3bmye9v617n">
                  <a href={`#${section.id}`} onClick={event => {
                event.preventDefault();
                scrollToSection(section.id);
              }} aria-current={isActive ? 'true' : undefined} className={['relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200', isActive ? 'bg-[#10b981]/10 font-semibold text-[#10b981]' : 'text-white/70 hover:bg-white/[0.06] hover:text-white'].join(' ')} data-id="element-146" data-mp="1k2ab47l0xaov">
                    {isActive && <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-[#10b981]" data-id="element-147" data-mp="hhjm51pryem9" />}
                    <StepDot number={section.number} done={isDone(section.id)} data-id="element-148" data-mp="2p4ba07w5tsov" />
                    <span className="truncate" data-id="element-149" data-mp="2t4v5l7134o8w">{section.navLabel}</span>
                  </a>
                </li>;
          })}
          </ul>

          <a href="#need-a-hand" onClick={event => {
          event.preventDefault();
          scrollToSection('need-a-hand');
        }} className="mt-5 flex items-center justify-center rounded-lg border border-[#10b981]/30 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#10b981] transition-colors duration-200 hover:border-[#10b981] hover:bg-[#10b981]/10" data-id="element-150" data-mp="2y4renkon35vb">
            Need a hand?
          </a>
        </div>
      </nav>
    </>;
}